/**
 * The notification centre: a bell in the top bar with the unread count, and a panel with
 * every notification the hub raised (the in-app feed), Needs you first, with Approve, Deny,
 * Acknowledge and Open where they apply. Opening it marks everything read for this sign-in.
 * The console's notices (a stale build, alerts off, an incident) sit at the top of the same panel:
 * one entry in the top bar, never a second count beside it.
 */
import { Bell, BellOff, Check, CircleCheck, GitMerge, Hand, Layers, Newspaper, ServerOff, Settings2, ShieldAlert, SquareTerminal, Users, Wallet, WifiOff } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { ApprovalRequest, EscalationView, Notification, Urgency } from '@orgus/protocol';
import {
  Button,
  EmptyState,
  GROUP_INDENT_SHIFT,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Pressable,
  RelativeTime,
  Segmented,
  SkeletonRows,
  cn,
  toast,
  useNow,
} from '@orgus/ui';
import { useRegisterCommands } from '../../lib/commands.ts';
import { useOrgus, useConnection } from '../../lib/store.ts';
import { isStale } from '../../lib/stream.ts';
import { useDecision } from '../approvals/ApprovalAnswer.tsx';
import { approvalOfUrl, openAnswer } from '../approvals/AnswerSheet.tsx';
import { ACTION_LABEL, isToolRequest, needsConfirm } from '../approvals/decision.ts';
import { useApprovals } from '../mobile/hooks.ts';
import { ackEscalation, getInbox, markSeen, useNotifyState } from './api.ts';
import { SEEN_KEY, closedLabel, filterCentre, foldRepeats, groupByDay, kindLabel, openEscalations, topUnread, unreadCount } from './centre.ts';
import type { CentreTab, LiveRefs } from './centre.ts';
import { countsAsWaiting } from '@orgus/protocol';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { demoNotifyState } from './demo.ts';
import { NoticeList } from '../../components/shell/NoticesItem.tsx';
import { countedNotices, newestNoticeSince, noticesLabel, sortNotices, topTone, useNoticeStore } from '../../components/shell/notices.ts';

/** What raised it, as an icon: the kind says what it is about; the colour says how much it matters. */
const KIND_ICON: Record<string, ReactNode> = {
  'approval.pending': <Hand aria-hidden />,
  'session.waiting': <Hand aria-hidden />,
  'session.ended': <SquareTerminal aria-hidden />,
  'session.unmetered': <Wallet aria-hidden />,
  'machine.offline': <ServerOff aria-hidden />,
  'pipeline.failed': <GitMerge aria-hidden />,
  'budget.alert': <Wallet aria-hidden />,
  escalation: <Users aria-hidden />,
  digest: <Newspaper aria-hidden />,
  batch: <Layers aria-hidden />,
};

function iconOf(n: Pick<Notification, 'kind' | 'ref' | 'urgency'>): ReactNode {
  if (n.kind && KIND_ICON[n.kind]) return KIND_ICON[n.kind];
  if (n.ref?.type === 'approval') return <Hand aria-hidden />;
  return n.urgency === 'critical' ? <ShieldAlert aria-hidden /> : <Bell aria-hidden />;
}

/** Icons are grey, as in every calm inbox; only a critical item that is still open is red. */
export function toneOf(n: Pick<Notification, 'kind' | 'ref' | 'urgency' | 'title'>, closed: boolean): 'failed' | 'waiting' | 'neutral' {
  if (!closed && n.urgency === 'critical') return 'failed';
  return 'neutral';
}

const TONE_TEXT = { failed: 'text-failed', waiting: 'text-waiting', neutral: 'text-fg-subtle' } as const;

/** Urgency in words, for screen readers only: the icon colour carries it on screen. */
const URGENCY_WORD: Record<Urgency, string> = { critical: 'Critical', high: 'High', normal: 'Normal', low: 'Low' };

const readLocalSeen = () => Number(globalThis.localStorage?.getItem(SEEN_KEY) ?? 0) || 0;

/** Stop an escalation: on the hub, or in the store when this is demo data. */
async function acknowledge(key: string): Promise<void> {
  if (useOrgus.getState().mode !== 'mock') {
    await ackEscalation(key);
    return;
  }
  // Through applyExt, so the change reaches `full.ext` and survives the next live update.
  const st = useOrgus.getState();
  const n = st.full.ext.notify as { escalations?: EscalationView[] } | undefined;
  if (n) st.applyExt({ ext: 'notify', type: 'state', data: { ...n, escalations: (n.escalations ?? []).filter((e) => e.key !== key) } });
}

/** In demo mode the centre has demo notifications of its own. */
function useDemoNotify(): void {
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  useEffect(() => {
    if (mode !== 'mock' || phase !== 'ready') return;
    // Through applyExt: a slice set on the scoped view alone is wiped by the next update.
    const st = useOrgus.getState();
    if (!st.ext.notify) st.applyExt({ ext: 'notify', type: 'state', data: demoNotifyState(Date.now()) });
  }, [mode, phase]);
}

/** When this sign-in last opened the centre: from the hub, or this browser when the hub cannot say. */
function useLastSeen(enabled: boolean, mock: boolean): [number | null, (at: number) => void] {
  const [seen, setSeen] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (mock) {
      setSeen(readLocalSeen());
      return;
    }
    const ctrl = new AbortController();
    getInbox(ctrl.signal)
      .then((r) => setSeen(Math.max(r.lastSeenAt, 0)))
      .catch(() => {
        if (!ctrl.signal.aborted) setSeen(readLocalSeen());
      });
    return () => ctrl.abort();
  }, [enabled, mock]);
  const mark = useCallback(
    (at: number) => {
      setSeen((s) => Math.max(s ?? 0, at));
      globalThis.localStorage?.setItem(SEEN_KEY, String(at));
      if (!mock) void markSeen(at).catch(() => undefined);
    },
    [mock],
  );
  return [seen, mark];
}

/**
 * A pending request's answer in the bell, the shared model (ADR 0072): Allow once at once for a
 * tool request that is not high risk; Answer opens the answer sheet for everything else (the
 * pattern for a session allow, a rule, a deny note, a question, plan or form, the high-risk confirm).
 */
function BellAnswer({ a, onAnswer }: { a: ApprovalRequest; onAnswer: () => void }) {
  const machine = useOrgus((s) => s.machines[a.machineId]?.name);
  const c = useDecision(a, { machine });
  const quick = isToolRequest(a) && !needsConfirm(a, 'allow-once');
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      {quick && (
        <Button size="sm" variant="primary" icon={<Check />} loading={c.busy === 'allow-once'} disabled={!!c.busy} onClick={() => void c.run('allow-once')}>
          {ACTION_LABEL['allow-once']}
        </Button>
      )}
      <Button size="sm" variant={quick ? 'secondary' : 'primary'} disabled={!!c.busy} onClick={onAnswer}>
        Answer
      </Button>
      {c.error && <span role="alert" className="basis-full text-caption text-status-danger">{c.error}</span>}
    </span>
  );
}

function Row({
  n,
  more,
  unread,
  escalation,
  approval,
  closed,
  onOpen,
}: {
  n: Notification;
  /** Older notifications with the same title, folded into this row. */
  more: number;
  unread: boolean;
  escalation: EscalationView | undefined;
  approval: ApprovalRequest | undefined;
  /** 'Answered' or 'Resolved' when the item is closed now, else null. */
  closed: string | null;
  onOpen: (url: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const kind = kindLabel(n);
  const canDecide = !!approval && approval.state === 'pending';
  const offers = new Set((n.actions ?? []).map((a) => a.id));
  // Every pending request is answered here or in the answer sheet (ADR 0072), whatever the notification offered.
  const decides = canDecide;
  const run = async (what: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(what);
    try {
      await fn();
      toast.success(ok, n.title);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  // One row: the kind icon in the colour of what it needs, the title, one line of detail and the
  // time. The whole row opens it; buttons appear only where a decision can be made right here.
  return (
    <li className="relative" data-notification={n.id}>
      <Pressable
        onClick={() => onOpen(n.url)}
        className="flex w-full items-start gap-3 rounded-control px-3 py-3 text-left transition-colors duration-(--duration-fast) hover:bg-hover"
      >
        <span className={cn('flex h-5 shrink-0 items-center [&_svg]:size-4', TONE_TEXT[toneOf(n, !!closed)])}>{iconOf(n)}</span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-baseline gap-2">
            <span className={cn('min-w-0 truncate text-ui', unread ? 'font-medium text-fg' : 'text-fg-muted')}>
              <span className="sr-only">
                {unread ? 'Unread. ' : ''}
                {URGENCY_WORD[n.urgency]}
                {kind ? `, ${kind}` : ''}:{' '}
              </span>
              {n.title}
            </span>
            {more > 0 && <span className="shrink-0 text-caption text-fg-subtle num">+{more}</span>}
            <RelativeTime ts={n.at} className="ml-auto shrink-0 text-caption text-fg-subtle" />
            <span aria-hidden className={cn('size-1.5 shrink-0 rounded-pill', unread ? 'bg-accent' : 'bg-transparent')} />
          </span>
          <span className="flex min-w-0 items-center gap-2 pr-4 text-caption text-fg-subtle">
            {n.body && <span className="min-w-0 truncate">{n.body.split('\n')[0]!.replace(/^[-*\u2022]\s+/, '')}</span>}
            {closed && (
              <span className="ml-auto flex shrink-0 items-center gap-1">
                <CircleCheck className="size-3.5" aria-hidden />
                {closed}
              </span>
            )}
          </span>
        </span>
      </Pressable>
      {(decides || escalation) && (
        <div className="-mt-1 flex items-center gap-2 pb-3 pl-12 pr-3">
          {canDecide && <BellAnswer a={approval!} onAnswer={() => onOpen(n.url)} />}
          {escalation && (
            <Button size="sm" variant="secondary" loading={busy === 'ack'} onClick={() => void run('ack', () => acknowledge(escalation.key), 'Acknowledged')}>
              Acknowledge
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

export function NotificationCentre() {
  useDemoNotify();
  const notify = useNotifyState();
  const approvals = useApprovals();
  const { mode, phase, connection } = useConnection();
  const mock = mode === 'mock';
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<CentreTab>('all');
  const [showNotices, setShowNotices] = useState(false);
  const [lastSeen, markRead] = useLastSeen(!!notify, mock);
  /** The mark as it was when the panel opened: rows newer than it stay highlighted while open. */
  const [shownSince, setShownSince] = useState(0);
  const navigate = useNavigate();
  const now = useNow(60_000);
  const listRef = useRef<HTMLDivElement>(null);

  const recent = notify?.recent ?? [];
  const escalations = useMemo(() => openEscalations(notify?.escalations), [notify?.escalations]);
  const openKeys = useMemo(() => new Set(escalations.keys()), [escalations]);
  const pendingById = useMemo(() => new Map((approvals?.pending ?? []).map((a) => [a.id, a])), [approvals]);
  const sessions = useOrgus((s) => s.sessions);
  const machines = useOrgus((s) => s.machines);
  // The one selector (ADR 0056): an old, dismissed or snoozed question is not open any more.
  const levels = useNeedsYouState().bySession;
  // What is still open, so Needs you leaves out what was answered. Demo items point at nothing real.
  const live = useMemo<LiveRefs | undefined>(
    () =>
      mock
        ? undefined
        : {
            pendingApprovals: approvals ? new Set(pendingById.keys()) : null,
            sessions: phase === 'ready' ? new Map(Object.values(sessions).map((x) => [x.id, x.status === 'waiting' && !countsAsWaiting(levels.get(x.id)) ? 'idle' : x.status])) : null,
            machines: phase === 'ready' ? new Map(Object.values(machines).map((m) => [m.id, m.online])) : null,
          },
    [mock, approvals, pendingById, phase, sessions, machines, levels],
  );
  const unread = lastSeen === null ? 0 : unreadCount(recent, lastSeen, live);
  // The console's own notices, in the same entry: their count adds to the badge.
  const noticeById = useNoticeStore((s) => s.byId);
  const notices = useMemo(() => sortNotices(Object.values(noticeById)), [noticeById]);
  // Only what is new since the bell was last opened counts and colours it (QA-6); every notice stays listed.
  const counted = useMemo(() => countedNotices(notices, lastSeen), [notices, lastSeen]);
  const noticeTone = topTone(counted);
  const top = lastSeen === null ? null : topUnread(recent, lastSeen);
  const list = filterCentre(recent, tab, openKeys, live);
  const needsCount = filterCentre(recent, 'needs', openKeys, live).length;

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    if (o) {
      setShownSince(lastSeen ?? 0);
      const newest = Math.max(recent[0]?.at ?? 0, Math.min(newestNoticeSince(notices), Date.now()));
      if (newest && newest > (lastSeen ?? 0)) markRead(newest);
    }
  };

  useRegisterCommands(
    [
      {
        id: 'notify.open',
        title: 'Open notifications',
        subtitle: unread ? `${unread} unread` : undefined,
        group: 'General',
        icon: Bell,
        keywords: ['inbox', 'alerts', 'escalations', 'needs you'],
        enabled: !!notify,
        perform: () => onOpenChange(true),
      },
    ],
    [unread, !!notify, recent[0]?.at, lastSeen],
  );

  const go = (url: string) => {
    setOpen(false);
    // A pending request is answered where you are, in the answer sheet (ADR 0072).
    const id = approvalOfUrl(url);
    if (id && pendingById.has(id)) openAnswer(id);
    else navigate(url);
  };

  const loading = phase === 'loading' || (!!notify && lastSeen === null);
  const stale = !mock && isStale(connection);
  const count = unread + counted.length;
  // The name holds the visible count (WCAG 2.5.3), then what it is made of.
  const parts = [notify ? (unread ? `${unread} unread` : 'all read') : '', counted.length ? noticesLabel(counted.length) : ''].filter(Boolean).join(', ');
  const label = `Notifications${count ? `, ${count > 99 ? '99+' : count}` : ''}${parts ? `: ${parts}` : ''}`;
  // The badge stays neutral; a small dot inside it says when something failed or needs action.
  const dot = top === 'critical' || noticeTone === 'danger' ? 'bg-status-danger-fill' : top === 'high' || noticeTone === 'attention' ? 'bg-status-attention-fill' : null;

  let body: ReactNode;
  if (loading) body = <SkeletonRows rows={4} className="p-4" />;
  else if (!notify)
    body = (
      <EmptyState
        icon={<BellOff />}
        title="Notifications are not running on this hub"
        description={phase === 'error' ? 'The hub did not answer, so there is nothing to show yet. Check that Orgus is running.' : 'Update the hub to a version with the notify module, then reload.'}
      />
    );
  else if (!list.length)
    body = (
      <EmptyState
        icon={<CircleCheck />}
        title={tab === 'needs' ? 'Nothing needs you' : 'You are all caught up'}
        description={tab === 'needs' ? 'Approvals, waiting sessions, offline machines and budget alerts land here.' : 'Everything Orgus raises shows here, even with push off.'}
      />
    );
  else
    body = groupByDay(list, now).map((g) => (
      <section key={g.label} aria-label={g.label}>
        <h3 className="sticky top-0 z-(--z-sticky) flex h-8 items-center bg-raised px-4 text-caption font-medium text-fg-subtle">{g.label}</h3>
        {/* A day's notifications sit one group step in under its heading (GROUP_INDENT_SHIFT). */}
        <ul className={cn('flex flex-col px-1 pb-1', GROUP_INDENT_SHIFT)}>
          {foldRepeats(g.items).map(({ n, more }) => (
            <Row
              key={n.id}
              n={n}
              more={more.length}
              unread={n.at > shownSince || more.some((m) => m.at > shownSince)}
              escalation={n.ref ? escalations.get(`${n.ref.type}:${n.ref.id}`) : undefined}
              approval={n.ref?.type === 'approval' ? pendingById.get(n.ref.id) : undefined}
              closed={escalations.has(`${n.ref?.type}:${n.ref?.id}`) ? null : closedLabel(n, live)}
              onOpen={go}
            />
          ))}
        </ul>
      </section>
    ));

  return (
    <>
    {/* Present from the first paint so a notice arriving later is announced once, politely. */}
    <span aria-live="polite" aria-atomic="true" className="sr-only">
      {notices.length > 0 ? noticesLabel(notices.length) : ''}
    </span>
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className={cn('relative flex h-control-sm min-w-control-sm shrink-0 items-center justify-center gap-1 rounded-control text-fg-subtle', count > 0 && 'px-2', 'transition-colors duration-(--duration-fast) hover:bg-hover hover:text-fg data-[state=open]:bg-hover data-[state=open]:text-fg')}
        >
          <Bell className="size-4" aria-hidden />
          {/* A quiet count beside the bell, as in the rail; the status shows only as a small dot. */}
          {count > 0 && (
            <span data-bell-count={count} className="num flex items-center gap-1 text-caption font-medium text-fg-muted" aria-hidden>
              {dot && <span data-bell-dot="" className={cn('size-1.5 shrink-0 rounded-pill', dot)} />}
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="flex max-h-[min(640px,80vh)] w-[min(420px,calc(100vw-16px))] flex-col" aria-label="Notifications">
        <header className="flex items-center gap-2 border-b border-border-subtle py-2 pl-4 pr-2">
          <h2 className="flex-1 text-ui font-semibold text-fg">Notifications</h2>
          <Segmented<CentreTab | 'notices'>
            label="Show"
            size="sm"
            value={showNotices && notices.length ? 'notices' : tab}
            onChange={(v) => {
              setShowNotices(v === 'notices');
              if (v !== 'notices') setTab(v);
            }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'needs', label: 'Needs you', ...(needsCount ? { count: needsCount } : {}) },
              // The console's notices, one tab away rather than a second entry in the top bar.
              ...(notices.length ? [{ value: 'notices' as const, label: 'Notices', count: notices.length }] : []),
            ]}
          />
          <IconButton size="sm" label="Notification settings" icon={<Settings2 />} onClick={() => go('/settings/notifications')} />
        </header>
        {stale && notify && (
          <p role="status" className="flex items-center gap-2 border-b border-border-subtle bg-waiting-soft px-4 py-2 text-caption text-waiting">
            <WifiOff className="size-3.5 shrink-0" aria-hidden />
            Showing the last list the hub sent. Reconnecting.
          </p>
        )}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {showNotices && notices.length > 0 ? <NoticeList notices={notices} onNavigate={go} /> : body}
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
}
